'use client';

import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { getDistanceLabel, getMonthsLabel } from '@core/Utils/Calculations/getCalculationLabels';

import { fetchCalculation } from '../Components/FinancingCalculator/Helpers/FetchCalculation';
import { getVehicleScopedDeposit } from '../Utils/GetVehicleScopedDeposit';
import { calculationAtom, calculationWithStorageAtom } from './Calculation.atom';
import { finCalcDataFieldValueAtom } from './FinCalcDataFieldValue.atom';
import { isCalculatorLoadingAtom } from './IsCalculatorLoading.atom';
import { isEmptyPayloadAtom } from './isEmptyPayload.atom';
import { selectedProductIdAtom } from './SelectedProductId.atom';

const additionalServicesAtom = finCalcDataFieldValueAtom('additionalServices');
const depositAtom = finCalcDataFieldValueAtom('deposit');
const mileageAtom = finCalcDataFieldValueAtom('mileage');
const termsAtom = finCalcDataFieldValueAtom('terms');
const persistedVehicleIdAtom = finCalcDataFieldValueAtom('vehicleId');
export const getCalculationAtom = atomFamily((vehicleId: string) =>
  atom(null, async (get, set) => {
    const additionalServices = get(additionalServicesAtom);
    const deposit = get(depositAtom);
    const locale = get(localeAtom)!;
    const mileage = get(mileageAtom);
    const persistedVehicleId = get(persistedVehicleIdAtom);
    const selectedProductId = get(selectedProductIdAtom)!;
    const terms = get(termsAtom);
    const vehicle = get(currentVehicleAtom)!;
    const isEmpty = get(isEmptyPayloadAtom);
    const source = get(tenantAtom);
    const depositForVehicle = getVehicleScopedDeposit({
      currentVehicleId: vehicleId,
      deposit,
      persistedVehicleId,
    });
    if (!locale || !vehicle) return null;
    set(isCalculatorLoadingAtom, true);
    try {
      const calculation = await fetchCalculation({
        locale,
        selectedProductId,
        deposit: depositForVehicle,
        terms,
        mileage,
        vehicleId,
        defaultDeposit: vehicle?.finance?.deposit,
        defaultMonths: vehicle?.finance?.months,
        defaultMileage: vehicle?.finance?.mileage,
        vehiclePrice: vehicle.pricing.price,
        additionalServices,
        isEmpty,
        source,
      });
      set(calculationAtom, calculation);
      set(calculationWithStorageAtom, calculation);
      set(persistedVehicleIdAtom, vehicleId);
      set(isCalculatorLoadingAtom, false);

      const currentProductId = calculation?.selectedProduct;
      const product = calculation?.products?.[currentProductId];

      if (!selectedProductId && calculation?.selectedProduct) {
        set(selectedProductIdAtom, calculation?.selectedProduct);
      }

      if (product && terms?.value !== product.inputs.months.value) {
        const value = product.inputs.months.value;
        const label = getMonthsLabel(value, locale);
        set(termsAtom, { value, label });
      }

      if (product && depositForVehicle !== product.inputs.deposit.value) {
        const value = product.inputs.deposit.value;
        set(depositAtom, value);
      }

      if (
        product &&
        (selectedProductId === 'threeWayFinancing' ||
          selectedProductId === 'twoWayFinancing' ||
          selectedProductId === 'privateLeasing' ||
          selectedProductId === 'PCP') &&
        mileage?.value !== product.inputs.mileage.value
      ) {
        const value = product.inputs.mileage.value;
        const label = getDistanceLabel(value, locale);
        set(mileageAtom, { value, label });
      }

      return calculation;
    } catch (error) {
      set(isCalculatorLoadingAtom, false);
      throw error;
    }
  }),
);
